import { assert, NormalizedNumber } from '@sparkdotfi/common-universal'

import { TokensToDepositRow } from '../components/tokens-to-deposit-panel/TokensToDepositPanel'
import { useSpkFarmData } from '../data/useSpkFarmData'
import { claimDialogConfig } from '../dialogs/claim/ClaimDialog'
import { stakeDialogConfig } from '../dialogs/stake/StakeDialog'
import { unstakeDialogConfig } from '../dialogs/unstake/UnstakeDialog'
import { MainPanelData, SpkFarmChartDetails, SpkFarmGeneralStatsResult } from '../types'
import { calculateFarmApy } from '../utils/calculateFarmApy'
import { calculateReward as _calculateReward } from '../utils/calculateReward'
import { useSpkFarmChartsDetails } from './useSpkFarmChartDetails'
import { useSpkFarmGeneralStats } from './useSpkFarmGeneralStats'
import { getChainConfigEntry } from '@/config/chain'
import { useGetBlockExplorerAddressLink } from '@/domain/hooks/useGetBlockExplorerAddressLink'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useConnectModal } from '@/domain/hooks/wallet'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { useOpenDialog } from '@/domain/state/dialogs/hooks'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { useSpkTokenRepository } from '@/features/spk/common/useSpkTokenRepository'

export interface UseSpkFarmResult {
  chainId: number
  generalStatsResult: SpkFarmGeneralStatsResult
  mainPanelData: MainPanelData
  chartDetails: SpkFarmChartDetails
  tokensToDepositRow: TokensToDepositRow
}

export function useSpkFarm(): UseSpkFarmResult {
  const { account } = useConnectedAddress({ throwOnNotConnected: false })
  const { chainId } = usePageChainId()
  const tokenRepository = useSpkTokenRepository({ chainId, account })
  const { openConnectModal = () => {} } = useConnectModal()
  const { openSandboxModal } = useSandbox()
  const openDialog = useOpenDialog()
  const getBlockExplorerLink = useGetBlockExplorerAddressLink()

  const chartDetails = useSpkFarmChartsDetails({ chainId })
  const generalStatsResult = useSpkFarmGeneralStats({ chainId })
  const spkFarm = useSpkFarmData({ chainId, tokenRepository })

  const { spk: spkFeatureConfig } = getChainConfigEntry(chainId)
  assert(spkFeatureConfig, 'Spk feature config is not defined on this chain')

  const spk = tokenRepository.findOneBySymbol(TokenSymbol('SPK'))
  const usds = tokenRepository.findOneBySymbol(TokenSymbol('USDS'))
  const usdsBalance = tokenRepository.findOneBalanceOrZero(TokenSymbol('USDS'))

  const apy = calculateFarmApy(spkFarm)

  function openStakeDialog(): void {
    openDialog(stakeDialogConfig, {})
  }

  function openUnstakeDialog(): void {
    openDialog(unstakeDialogConfig, {})
  }

  function openClaimDialog(): void {
    openDialog(claimDialogConfig, {})
  }

  const mainPanelData: MainPanelData = (() => {
    if (!account) {
      return {
        type: 'cta',
        props: {
          type: 'disconnected',
          apy,
          connectWallet: openConnectModal,
          tryInSandbox: openSandboxModal,
        },
      } satisfies MainPanelData
    }

    if (spkFarm.staked.eq(0) && spkFarm.earned.eq(0)) {
      return {
        type: 'cta',
        props: {
          type: 'connected',
          apy,
          openStakeDialog,
          usdsBalance,
        },
      } satisfies MainPanelData
    }

    function calculateReward(timestampInMs: number): NormalizedNumber {
      return _calculateReward({
        earned: spkFarm.earned,
        staked: spkFarm.staked,
        rewardRate: spkFarm.rewardRate,
        earnedTimestamp: spkFarm.earnedTimestamp,
        periodFinish: spkFarm.periodFinish,
        timestampInMs,
        totalSupply: spkFarm.totalSupply,
      })
    }

    const refreshGrowingRewardIntervalInMs =
      spkFarm.rewardRate.gt(0) && spkFarm.staked.gt(0) ? GROWING_REWARD_REFRESH_INTERVAL_IN_MS : undefined

    return {
      type: 'active',
      props: {
        apy,
        calculateReward,
        canClaim: spkFarm.earned.gt(0) || spkFarm.rewardRate.gt(0),
        openClaimDialog,
        openStakeDialog,
        openUnstakeDialog,
        rewardToken: spk,
        stakingToken: usds,
        stakedAmount: spkFarm.staked,
        refreshGrowingRewardIntervalInMs,
      },
    } satisfies MainPanelData
  })()

  const tokensToDepositRow: TokensToDepositRow = {
    token: usds,
    balance: usdsBalance,
    blockExplorerLink: getBlockExplorerLink(usds.address) ?? '/',
    openStakeDialog,
  }

  return {
    chainId,
    generalStatsResult,
    chartDetails,
    mainPanelData,
    tokensToDepositRow,
  }
}

const GROWING_REWARD_REFRESH_INTERVAL_IN_MS = 50
